'use client';

import { forwardRef, useEffect, useRef, type TextareaHTMLAttributes } from 'react';
import styles from './textarea.module.css';

/**
 * Textarea - the design-system multi-line input: the Input's chrome and the
 * shared control tokens, grown to hold prose. Same modifiers as Input
 * (`size`, `quiet`, `invalid`), plus:
 *
 *   - `autoGrow` keeps the box sized to its content (no scrollbar, no manual
 *     resize handle) - the inline-edit pattern of the description field.
 *
 * Renders a real `<textarea>`, forwards its ref, passes native props through.
 */

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  size?: 'sm' | 'md';
  /** Inline-edit look: chromeless at rest, chrome on hover/focus. */
  quiet?: boolean;
  /** Signal an invalid value (border + aria-invalid); add a message nearby. */
  invalid?: boolean;
  /** Track the content height instead of scrolling. */
  autoGrow?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { size = 'md', quiet, invalid, autoGrow, className, ...rest },
  ref,
) {
  const inner = useRef<HTMLTextAreaElement | null>(null);

  // Re-measure on every commit: the value is controlled by the caller, so any
  // render may have changed the content (cheap - two style writes, no reads
  // besides scrollHeight).
  useEffect(() => {
    const el = inner.current;
    if (!el || !autoGrow) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  });

  return (
    <textarea
      ref={(el) => {
        inner.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      className={className ? `${styles.textarea} ${className}` : styles.textarea}
      data-size={size}
      data-quiet={quiet || undefined}
      data-invalid={invalid || undefined}
      data-autogrow={autoGrow || undefined}
      aria-invalid={invalid || undefined}
      {...(autoGrow ? { rows: 1 } : {})}
      {...rest}
    />
  );
});
